import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "@/redux/store";
import { selectIsAuthenticated } from "@/redux/slices/authSlice";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

/**
 * ProtectedRoute - Ensures user is authenticated before accessing content
 * Simple and fast - just checks Redux state (no API calls)
 * AuthProvider handles all authentication initialization
 */
export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector((state) => state.auth.user);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Email / OAuth sign-ups without a workspace complete company details in onboarding
  if (user?.needsCompanySetup && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // Check SUPER_ADMIN access if required
  if (requireSuperAdmin && user?.role !== "SUPER_ADMIN") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-500 mb-6">
            This page is only accessible to Reacho team members. SUPER_ADMIN access required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-sec text-white rounded-lg hover:bg-(--accent-hover) transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check admin access if required (for customer admins)
  if (requireAdmin && user?.role !== "ADMIN" && user?.role !== "SUPER_ADMIN") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-500 mb-6">
            You don't have permission to access this page. Admin access required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-sec text-white rounded-lg hover:bg-(--accent-hover) transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
